/**
 * OpsDashboard Component
 *
 * Operational dashboard for Care Coordinators
 * Focus: Day-to-day workflow efficiency, workload management, blocker resolution
 */

import React, { useMemo, useState } from 'react';
import { Box, Grid, Typography, Chip } from '@mui/material';
import { usePredictContext } from '~/hooks/predict/usePredictContext';
import { AnalyticsFilters } from '~/types/analytics';
import { WorkflowStage, DisqualificationReason } from '~/types/predict';
import {
  generateMockAnalyticsData,
  calculateDerivedMetrics,
  applyFilters,
} from '~/utils/generateMockAnalytics';
import ChartContainer from './ChartContainer';
import WorkflowFunnel from './WorkflowFunnel';
import BlockersChart from './BlockersChart';
import DisqualificationChart from './DisqualificationChart';
import AIInsights, { Insight, Recommendation } from './AIInsights';
import AnalyticsPatientTable, { DrillDownFilter } from './AnalyticsPatientTable';

interface OpsDashboardProps {
  filters: AnalyticsFilters;
}

const OpsDashboard: React.FC<OpsDashboardProps> = ({ filters }) => {
  const { currentUser } = usePredictContext();
  const [aiInsightsExpanded, setAiInsightsExpanded] = useState(false);
  const [drillDownFilter, setDrillDownFilter] = useState<DrillDownFilter | null>(null);

  // Generate mock data once (cached)
  const allRecords = useMemo(() => generateMockAnalyticsData(300), []);

  // Filter by user's accessible facilities and analytics filters
  const filteredRecords = useMemo(() => {
    // First, filter by user's accessible facilities
    const userAccessibleFacilities = currentUser?.clientOrganizations.map(c => c.id) || [];
    const accessibleRecords = allRecords.filter(record =>
      userAccessibleFacilities.includes(record.facilityId)
    );

    // Then apply analytics filters
    return applyFilters(accessibleRecords, filters, currentUser?.id);
  }, [allRecords, filters, currentUser]);

  // Apply drill-down filter on top of base filters for chart data
  const displayRecords = useMemo(() => {
    if (!drillDownFilter) return filteredRecords;

    if (drillDownFilter.customFilter) {
      return filteredRecords.filter(drillDownFilter.customFilter);
    }

    if (drillDownFilter.stage) {
      return filteredRecords.filter(r => r.currentStage === drillDownFilter.stage);
    }

    return filteredRecords;
  }, [filteredRecords, drillDownFilter]);

  // Calculate KPI metrics (from displayRecords so they update with drill-down filters)
  const kpis = useMemo(() => {
    const now = new Date();
    const last24h = new Date(now.getTime() - 24 * 60 * 60 * 1000);

    // New candidates in last 24h
    const newCandidates = displayRecords.filter(
      r => new Date(r.admissionTimestamp) >= last24h
    ).length;

    // High priority patients (score >= 75)
    const highPriority = displayRecords.filter(
      r => r.programScore >= 75 && r.currentStage !== WorkflowStage.DISQUALIFIED && !r.transitionedAt
    ).length;

    // Patients ready to advance (readiness = 100%)
    const readyToAdvance = displayRecords.filter(
      r => r.readinessPercent === 100 && r.currentStage !== WorkflowStage.TRANSITION && !r.transitionedAt
    ).length;

    // Backlog > 48h
    const backlog = displayRecords.filter(r => {
      const metrics = calculateDerivedMetrics(r);
      return metrics.isBacklog;
    }).length;

    return { newCandidates, highPriority, readyToAdvance, backlog };
  }, [displayRecords]);

  // Calculate workflow funnel data
  // Use filteredRecords for stage filters to keep funnel stable
  // Use displayRecords for non-stage filters (high priority, backlog, etc.) to show drill-down
  const funnelData = useMemo(() => {
    const stages = [
      WorkflowStage.TRIAGE,
      WorkflowStage.ELIGIBILITY,
      WorkflowStage.ACCEPTANCE,
      WorkflowStage.TRANSITION,
    ];

    const now = new Date();

    // Use filteredRecords for stage-based drill-downs, displayRecords for others
    const dataSource = drillDownFilter?.type === 'stage' || drillDownFilter?.label === 'Discharged Patients'
      ? filteredRecords
      : displayRecords;

    const stageData = stages.map((stage) => {
      // Only count patients who haven't been discharged yet
      // Discharged patients (transitionedAt set) should only appear in DISCHARGED stage
      const inStage = dataSource.filter(r => r.currentStage === stage && !r.transitionedAt);
      const count = inStage.length;

      // Calculate median time in stage ALWAYS from baseline (filteredRecords) for consistency
      // This provides a stable reference point regardless of drill-down filters
      const allInStage = filteredRecords.filter(r =>
        r.stageTransitions.some(t => t.stage === stage)
      );
      const times = allInStage
        .map(r => {
          const transition = r.stageTransitions.find(t => t.stage === stage);
          if (!transition || !transition.exitedAt) return null;
          const entered = new Date(transition.enteredAt).getTime();
          const exited = new Date(transition.exitedAt).getTime();
          return (exited - entered) / (1000 * 60 * 60); // hours
        })
        .filter((t): t is number => t !== null);
      times.sort((a, b) => a - b);
      const medianTimeHours = times.length > 0 ? times[Math.floor(times.length / 2)] : 0;

      // Calculate aging threshold: average + 1 standard deviation (from baseline data)
      let agingThresholdHours = 24; // default fallback
      if (times.length > 0) {
        const average = times.reduce((sum, t) => sum + t, 0) / times.length;
        const variance = times.reduce((sum, t) => sum + Math.pow(t - average, 2), 0) / times.length;
        const stdDev = Math.sqrt(variance);
        agingThresholdHours = average + stdDev;
      }

      const agingThresholdMs = agingThresholdHours * 60 * 60 * 1000;
      const agingThresholdDate = new Date(now.getTime() - agingThresholdMs);

      // Calculate aging count: from current displayed patients (respects drill-down filter)
      // Only count patients who haven't been discharged yet
      const agingCount = inStage.filter(r => {
        if (r.transitionedAt) return false; // Skip discharged patients
        const transition = r.stageTransitions.find(t => t.stage === stage);
        if (!transition) return false;
        const enteredAt = new Date(transition.enteredAt);
        return enteredAt < agingThresholdDate;
      }).length;

      return {
        stage: stage.replace(/_/g, ' '),
        count,
        medianTimeHours,
        agingCount,
      };
    });

    // Add Discharged stage (completed patients)
    const dischargedPatients = dataSource.filter(r => r.transitionedAt);
    const dischargedCount = dischargedPatients.length;

    // Calculate median time from admission to discharge (always from baseline for consistency)
    const baselineDischargedPatients = filteredRecords.filter(r => r.transitionedAt);
    const dischargeTimes = baselineDischargedPatients
      .map(r => {
        const admitted = new Date(r.admissionTimestamp).getTime();
        const discharged = new Date(r.transitionedAt!).getTime();
        return (discharged - admitted) / (1000 * 60 * 60); // hours
      })
      .filter((t): t is number => t !== null);
    dischargeTimes.sort((a, b) => a - b);
    const dischargedMedianTimeHours = dischargeTimes.length > 0 ? dischargeTimes[Math.floor(dischargeTimes.length / 2)] : 0;

    stageData.push({
      stage: 'DISCHARGED',
      count: dischargedCount,
      medianTimeHours: dischargedMedianTimeHours,
      agingCount: 0, // No aging count for completed patients
    });

    return stageData;
  }, [filteredRecords, displayRecords, drillDownFilter]);

  // Calculate completed count (patients who transitioned within date range)
  const completedCount = useMemo(() => {
    return filteredRecords.filter(r => {
      if (!r.transitionedAt) return false;
      const transitionDate = new Date(r.transitionedAt);
      return transitionDate >= filters.dateRange.start && transitionDate <= filters.dateRange.end;
    }).length;
  }, [filteredRecords, filters.dateRange]);

  // Calculate blocker distribution
  const blockerData = useMemo(() => {
    const blockerCounts: Record<string, number> = {};

    displayRecords.forEach(record => {
      record.blockers.forEach(blocker => {
        blockerCounts[blocker] = (blockerCounts[blocker] || 0) + 1;
      });
    });

    return Object.entries(blockerCounts)
      .map(([label, count]) => ({ label, count }))
      .sort((a, b) => b.count - a.count);
  }, [displayRecords]);

  // Calculate disqualification distribution
  const disqualificationData = useMemo(() => {
    const reasonCounts: Record<string, number> = {};

    displayRecords
      .filter(r => r.disqualificationReason)
      .forEach(record => {
        const reason = record.disqualificationReason!;
        reasonCounts[reason] = (reasonCounts[reason] || 0) + 1;
      });

    const colors = ['#ef5350', '#ff7043', '#ffa726', '#ffca28', '#66bb6a', '#42a5f5'];

    return Object.entries(reasonCounts)
      .map(([reason, count], index) => ({
        reason: reason.replace(/_/g, ' '),
        count,
        color: colors[index % colors.length],
      }))
      .sort((a, b) => b.count - a.count);
  }, [displayRecords]);

  // Calculate personal workload metrics
  const workloadMetrics = useMemo(() => {
    const myRecords = displayRecords.filter(r => r.ownerId === currentUser?.id);
    const activeCases = myRecords.filter(r => !r.transitionedAt && r.currentStage !== WorkflowStage.DISQUALIFIED).length;

    // Median time to first review
    const reviewTimes = myRecords
      .filter(r => r.firstReviewTimestamp)
      .map(r => {
        const metrics = calculateDerivedMetrics(r);
        return metrics.timeToFirstReview || 0;
      })
      .sort((a, b) => a - b);
    const medianReviewTimeHours =
      reviewTimes.length > 0
        ? reviewTimes[Math.floor(reviewTimes.length / 2)] / (1000 * 60 * 60)
        : 0;

    // Cases handled per day (estimated)
    const completedCases = myRecords.filter(r => r.transitionedAt).length;
    const daysInRange = (filters.dateRange.end.getTime() - filters.dateRange.start.getTime()) / (1000 * 60 * 60 * 24);
    const casesPerDay = daysInRange > 0 ? completedCases / daysInRange : 0;

    // Override rate
    const overrides = myRecords.filter(r => r.selectedProgram && r.selectedProgram !== r.recommendedProgram).length;
    const overrideRate = myRecords.length > 0 ? (overrides / myRecords.length) * 100 : 0;

    return {
      activeCases,
      medianReviewTimeHours,
      casesPerDay,
      overrideRate,
    };
  }, [displayRecords, currentUser, filters]);

  // Generate AI insights based on operational metrics
  const aiInsights = useMemo(() => {
    const insights: Insight[] = [];
    const recommendations: Recommendation[] = [];
    let summary = '';

    // Analyze backlog with root cause analysis
    if (kpis.backlog > 0) {
      // Get backlog patients
      const backlogPatients = displayRecords.filter(r => {
        const metrics = calculateDerivedMetrics(r);
        return metrics.isBacklog;
      });

      // Analyze which stages have backlog
      const backlogByStage: Record<string, number> = {};
      backlogPatients.forEach(p => {
        const stageName = p.currentStage.replace(/_/g, ' ');
        backlogByStage[stageName] = (backlogByStage[stageName] || 0) + 1;
      });
      const topBacklogStage = Object.entries(backlogByStage).sort((a, b) => b[1] - a[1])[0];

      // Analyze blockers affecting backlog patients
      const backlogBlockers: Record<string, number> = {};
      backlogPatients.forEach(p => {
        p.blockers.forEach(blocker => {
          backlogBlockers[blocker] = (backlogBlockers[blocker] || 0) + 1;
        });
      });
      const topBacklogBlocker = Object.entries(backlogBlockers).sort((a, b) => b[1] - a[1])[0];

      // Identify unassigned backlog patients
      const unassignedBacklog = backlogPatients.filter(p => !p.ownerId).length;

      if (kpis.backlog > 5) {
        const rootCauses: string[] = [];
        if (topBacklogStage) {
          rootCauses.push(`${topBacklogStage[1]} patients stuck in ${topBacklogStage[0]}`);
        }
        if (topBacklogBlocker) {
          rootCauses.push(`${topBacklogBlocker[1]} patients blocked by "${topBacklogBlocker[0]}"`);
        }
        if (unassignedBacklog > 0) {
          rootCauses.push(`${unassignedBacklog} unassigned`);
        }

        insights.push({
          type: 'concern',
          title: 'Workflow Backlog Detected',
          description: `${kpis.backlog} backlog patients have been in their current stage for more than 48 hours. Root causes: ${rootCauses.join(', ')}. These aging cases require immediate attention to prevent further delays.`,
          dataPoints: [`${kpis.backlog} backlog patients`],
        });

        // Build specific action based on root cause
        let specificAction = '';
        if (topBacklogStage && topBacklogStage[0] === 'ELIGIBILITY') {
          specificAction = `Focus on Eligibility stage: ${topBacklogStage[1]} patients are aging here. Prioritize completing eligibility checklists, expedite insurance verifications, and ensure all required documentation is obtained. `;
        } else if (topBacklogStage && topBacklogStage[0] === 'ACCEPTANCE') {
          specificAction = `Focus on Acceptance stage: ${topBacklogStage[1]} patients are aging here. Expedite provider approvals, confirm patient/family acceptance, and finalize program selections. `;
        } else if (topBacklogStage && topBacklogStage[0] === 'TRIAGE') {
          specificAction = `Focus on Triage stage: ${topBacklogStage[1]} patients need initial assessment. Assign owners immediately and complete initial scoring within 4 hours of admission. `;
        } else if (topBacklogStage && topBacklogStage[0] === 'TRANSITION') {
          specificAction = `Focus on Transition stage: ${topBacklogStage[1]} patients are ready but not discharged. Coordinate final logistics, DME delivery, and home health agency schedules. `;
        }

        if (topBacklogBlocker) {
          specificAction += `Address "${topBacklogBlocker[0]}" affecting ${topBacklogBlocker[1]} backlog patients—this is the primary bottleneck. `;
        }

        if (unassignedBacklog > 0) {
          specificAction += `Assign ${unassignedBacklog} unassigned backlog patients immediately to active coordinators. `;
        }

        recommendations.push({
          priority: 'high',
          title: 'Clear Backlog with Targeted Interventions',
          action: specificAction + 'Hold a 30-minute standup to triage backlog cases, redistribute workload if needed, and escalate systemic blockers to leadership.',
          impact: 'Clearing backlog can improve patient satisfaction, reduce LOS by 0.5-1 day per patient, and prevent workflow congestion that compounds over time.',
        });
      } else if (kpis.backlog > 0 && kpis.backlog <= 5) {
        const rootCauses: string[] = [];
        if (topBacklogStage) {
          rootCauses.push(`in ${topBacklogStage[0]}`);
        }
        if (topBacklogBlocker) {
          rootCauses.push(`blocked by "${topBacklogBlocker[0]}"`);
        }

        insights.push({
          type: 'neutral',
          title: 'Small Backlog - Proactive Attention Needed',
          description: `${kpis.backlog} backlog patient${kpis.backlog > 1 ? 's have' : ' has'} been in their current stage for more than 48 hours${rootCauses.length > 0 ? ` (${rootCauses.join(', ')})` : ''}. Address these cases today to prevent backlog growth.`,
          dataPoints: [`${kpis.backlog} backlog patient${kpis.backlog > 1 ? 's' : ''}`],
        });
      }
    } else {
      insights.push({
        type: 'positive',
        title: 'No Backlog - Efficient Processing',
        description: 'All patients are progressing through workflow stages within expected timeframes. This indicates strong operational efficiency.',
      });
    }

    // Analyze high priority patients
    if (kpis.highPriority > 10) {
      insights.push({
        type: 'positive',
        title: 'Strong In-Home Care Candidates Identified',
        description: `${kpis.highPriority} high priority patients have disposition scores ≥75, indicating excellent suitability for in-home care programs. These high-priority candidates are likely to achieve successful outcomes with home-based services.`,
        dataPoints: [`${kpis.highPriority} high priority patients`],
      });
      recommendations.push({
        priority: 'medium',
        title: 'Expedite High-Score Patient Processing',
        action: 'Create a dedicated triage queue for high-score patients (≥75). Schedule morning huddles to review priority cases and assign ownership immediately upon admission. These patients should be fast-tracked through eligibility and acceptance stages.',
        impact: 'Faster processing of high-score patients can improve transition success rates, demonstrate clear value to clinical partners, and ensure ideal candidates receive timely home-based care.',
      });
    } else if (kpis.highPriority >= 5) {
      insights.push({
        type: 'positive',
        title: 'Quality In-Home Care Candidates',
        description: `${kpis.highPriority} high priority patients with disposition scores ≥75 are excellent candidates for in-home care programs. Focus on expediting their transitions to achieve optimal outcomes.`,
        dataPoints: [`${kpis.highPriority} high priority patients`],
      });
    }

    // Analyze ready to advance
    if (kpis.readyToAdvance > 5) {
      insights.push({
        type: 'positive',
        title: 'Multiple Patients Ready for Stage Advancement',
        description: `${kpis.readyToAdvance} ready patients have met all requirements and are ready to progress. Focus on timely stage transitions to maintain workflow velocity.`,
        dataPoints: [`${kpis.readyToAdvance} ready patients`],
      });
      recommendations.push({
        priority: 'medium',
        title: 'Accelerate Stage Transitions',
        action: 'Schedule 15-minute transition review sessions twice daily to advance ready patients. Use standardized checklist to verify readiness and document transition decisions.',
        impact: 'Reducing wait time between stages can shorten overall cycle time by 20-30% and improve patient experience.',
      });
    }

    // Analyze blockers
    const topBlocker = blockerData[0];
    if (topBlocker && displayRecords.length > 0 && topBlocker.count > displayRecords.length * 0.15) {
      // Get patients affected by this blocker
      const affectedPatients = displayRecords.filter(r => r.blockers.includes(topBlocker.label));

      // Create patient list with names and assigned coordinators (limit to first 5 for readability)
      const patientDetails = affectedPatients.slice(0, 5).map(p => {
        const coordinator = p.ownerName || 'Unassigned';
        return `${p.patientName} (${coordinator})`;
      }).join(', ');

      const morePatients = affectedPatients.length > 5 ? ` and ${affectedPatients.length - 5} more` : '';

      insights.push({
        type: 'concern',
        title: `"${topBlocker.label}" is Primary Workflow Blocker`,
        description: `This blocker affects ${topBlocker.count} patients (${(topBlocker.count / displayRecords.length * 100).toFixed(0)}% of cases), indicating a systemic issue requiring intervention. Affected patients: ${patientDetails}${morePatients}.`,
      });

      let blockerAction = '';
      if (topBlocker.label.toLowerCase().includes('insurance')) {
        blockerAction = 'Partner with financial counselors to expedite insurance verification. Consider pre-verification workflows for anticipated admissions or high-volume payers.';
      } else if (topBlocker.label.toLowerCase().includes('physician') && topBlocker.label.toLowerCase().includes('order')) {
        blockerAction = `Coordinate directly with attending physicians for the affected patients listed above. Implement daily physician rounding coordination, use EHR order sets for common discharge scenarios, and escalate to hospitalist or case management leadership for delays >24 hours. Assigned coordinators should reach out today to obtain missing orders.`;
      } else if (topBlocker.label.toLowerCase().includes('medical') || topBlocker.label.toLowerCase().includes('documentation')) {
        blockerAction = 'Collaborate with clinical teams to obtain required documentation earlier in the admission. Implement automated chart review flags for missing records and establish daily huddles with medical records staff.';
      } else if (topBlocker.label.toLowerCase().includes('dme') || topBlocker.label.toLowerCase().includes('equipment')) {
        blockerAction = 'Streamline DME ordering process by establishing preferred vendor relationships with expedited ordering. Create standardized order forms for common equipment needs and coordinate early with therapy teams for equipment assessments.';
      } else if (topBlocker.label.toLowerCase().includes('home safety') || topBlocker.label.toLowerCase().includes('evaluation')) {
        blockerAction = 'Accelerate home safety evaluations by requesting assessments within 24 hours of admission for likely discharge candidates. Partner with therapy teams and social work for rapid home assessments, including telehealth options when appropriate.';
      } else if (topBlocker.label.toLowerCase().includes('caregiver')) {
        blockerAction = 'Enhance caregiver readiness by providing structured training programs, video resources, and hands-on education sessions. Coordinate early family meetings and offer telehealth caregiver assessments to expedite the evaluation process.';
      } else if (topBlocker.label.toLowerCase().includes('family') || topBlocker.label.toLowerCase().includes('patient')) {
        blockerAction = 'Improve patient/family engagement strategies. Provide educational materials earlier, offer translation services, and schedule dedicated counseling sessions within 24h of admission.';
      } else {
        blockerAction = 'Conduct root cause analysis on this blocker type. Engage stakeholders (clinical, operations, payers) to identify solutions and implement preventive measures.';
      }

      recommendations.push({
        priority: 'high',
        title: `Address "${topBlocker.label}" Blocker`,
        action: blockerAction,
        impact: `Resolving this blocker could accelerate workflow for up to ${topBlocker.count} patients, reducing cycle time and improving conversion rates.`,
      });
    }

    // Analyze personal workload
    if (workloadMetrics.activeCases > 15) {
      insights.push({
        type: 'concern',
        title: 'High Personal Caseload',
        description: `You currently have ${workloadMetrics.activeCases} active cases in your caseload. Research shows case manager effectiveness decreases above 12-15 concurrent cases.`,
        dataPoints: [`${workloadMetrics.activeCases} active cases`],
      });
      recommendations.push({
        priority: 'high',
        title: 'Request Workload Rebalancing',
        action: 'Discuss workload distribution with your manager. Consider transferring stable cases to colleagues or requesting assignment freeze until case volume decreases to sustainable levels.',
        impact: 'Maintaining optimal caseload improves quality of care, reduces errors, and prevents burnout. Target 10-12 active cases for peak performance.',
      });
    } else if (workloadMetrics.activeCases < 5 && kpis.newCandidates > 5) {
      insights.push({
        type: 'neutral',
        title: 'Capacity Available for New Cases',
        description: `With ${workloadMetrics.activeCases} active cases in your caseload, you have capacity to take on additional patients from the ${kpis.newCandidates} new candidates.`,
        dataPoints: [`${workloadMetrics.activeCases} active cases`, `${kpis.newCandidates} new candidates`],
      });
    }

    // Analyze override rate
    if (workloadMetrics.overrideRate > 25) {
      insights.push({
        type: 'concern',
        title: 'High AI Recommendation Override Rate',
        description: `You're overriding AI recommendations ${workloadMetrics.overrideRate.toFixed(0)}% of the time. While clinical judgment is important, frequent overrides may indicate model misalignment.`,
      });
      recommendations.push({
        priority: 'low',
        title: 'Review Override Patterns',
        action: 'Document reasons for overrides in case notes. Share feedback with AI team to improve model accuracy. Consider whether specific patient populations consistently require overrides.',
        impact: 'Improving AI alignment reduces decision time and cognitive load while maintaining quality outcomes.',
      });
    } else if (workloadMetrics.overrideRate < 10) {
      insights.push({
        type: 'positive',
        title: 'Strong AI Recommendation Alignment',
        description: `Your ${workloadMetrics.overrideRate.toFixed(0)}% override rate indicates high confidence in AI recommendations and effective model training.`,
      });
    }

    // Generate summary
    const urgentCount = recommendations.filter(r => r.priority === 'high').length;

    if (urgentCount > 0) {
      summary = `Operational analysis reveals ${urgentCount} high-priority focus area${urgentCount > 1 ? 's' : ''} requiring immediate attention. You're managing ${workloadMetrics.activeCases} active cases with ${kpis.readyToAdvance} patients ready to advance. Primary concerns include ${insights.filter(i => i.type === 'concern')[0]?.title.toLowerCase() || 'workflow optimization'}. Addressing these issues will improve patient flow and outcomes.`;
    } else if (insights.filter(i => i.type === 'positive').length > insights.filter(i => i.type === 'concern').length) {
      summary = `Operations are running smoothly with ${workloadMetrics.activeCases} active cases and ${kpis.readyToAdvance} patients ready to advance. Key strengths include ${insights.filter(i => i.type === 'positive')[0]?.title.toLowerCase() || 'efficient case management'}. Continue current practices and focus on incremental improvements suggested below.`;
    } else {
      summary = `Currently managing ${workloadMetrics.activeCases} active cases with ${kpis.newCandidates} new candidates in the last 24 hours. ${kpis.backlog > 0 ? `${kpis.backlog} patients are aging beyond 48 hours` : 'No significant backlogs detected'}. Review recommendations below to optimize workflow efficiency.`;
    }

    return { insights, recommendations, summary };
  }, [kpis, workloadMetrics, blockerData, displayRecords]);

  return (
    <Box sx={{ pb: aiInsightsExpanded ? '50vh' : 0 }}>
      {/* Row 1: All KPIs (8 total) - Responsive Grid */}
      <Grid container spacing={3} sx={{ mb: 4, px: 2 }}>
        <Grid item xs={6} sm={4} md={3} lg={1.5}>
          <Box
            sx={{
              textAlign: 'center',
              cursor: 'pointer',
              p: 1,
              borderRadius: 1,
              transition: 'all 0.2s',
              bgcolor: drillDownFilter?.type === 'newCandidates' ? 'primary.light' : 'transparent',
              '&:hover': {
                bgcolor: 'action.hover',
              },
            }}
            onClick={() => {
              if (drillDownFilter?.type === 'newCandidates') {
                setDrillDownFilter(null);
              } else {
                const now = new Date();
                const last24h = new Date(now.getTime() - 24 * 60 * 60 * 1000);
                setDrillDownFilter({
                  type: 'newCandidates',
                  label: 'New Candidates (Last 24 Hours)',
                  customFilter: (r) => new Date(r.admissionTimestamp) >= last24h,
                });
              }
            }}
          >
            <Typography variant="caption" color="grey.500" sx={{ textTransform: 'uppercase', fontWeight: 600, letterSpacing: 0.5, display: 'block', minHeight: 32, lineHeight: 1.2, mb: 0.5 }}>
              New Candidates (24h)
            </Typography>
            <Box sx={{ width: '100%', height: 1, bgcolor: 'divider', mb: 1 }} />
            <Typography variant="h5" sx={{ fontWeight: 600 }}>{kpis.newCandidates}</Typography>
          </Box>
        </Grid>
        <Grid item xs={6} sm={4} md={3} lg={1.5}>
          <Box
            sx={{
              textAlign: 'center',
              cursor: 'pointer',
              p: 1,
              borderRadius: 1,
              transition: 'all 0.2s',
              bgcolor: drillDownFilter?.type === 'highPriority' ? 'primary.light' : 'transparent',
              '&:hover': {
                bgcolor: 'action.hover',
              },
            }}
            onClick={() => {
              if (drillDownFilter?.type === 'highPriority') {
                setDrillDownFilter(null);
              } else {
                setDrillDownFilter({
                  type: 'highPriority',
                  label: 'High Priority Patients (Score ≥ 75)',
                  customFilter: (r) => r.programScore >= 75 && r.currentStage !== WorkflowStage.DISQUALIFIED && !r.transitionedAt,
                });
              }
            }}
          >
            <Typography variant="caption" color="grey.500" sx={{ textTransform: 'uppercase', fontWeight: 600, letterSpacing: 0.5, display: 'block', minHeight: 32, lineHeight: 1.2, mb: 0.5 }}>
              High Priority
            </Typography>
            <Box sx={{ width: '100%', height: 1, bgcolor: 'divider', mb: 1 }} />
            <Typography variant="h5" sx={{ fontWeight: 600 }}>{kpis.highPriority}</Typography>
          </Box>
        </Grid>
        <Grid item xs={6} sm={4} md={3} lg={1.5}>
          <Box
            sx={{
              textAlign: 'center',
              cursor: 'pointer',
              p: 1,
              borderRadius: 1,
              transition: 'all 0.2s',
              bgcolor: drillDownFilter?.type === 'ready' ? 'primary.light' : 'transparent',
              '&:hover': {
                bgcolor: 'action.hover',
              },
            }}
            onClick={() => {
              if (drillDownFilter?.type === 'ready') {
                setDrillDownFilter(null);
              } else {
                setDrillDownFilter({
                  type: 'ready',
                  label: 'Ready to Advance (100% Readiness)',
                  customFilter: (r) => r.readinessPercent === 100 && r.currentStage !== WorkflowStage.TRANSITION && !r.transitionedAt,
                });
              }
            }}
          >
            <Typography variant="caption" color="grey.500" sx={{ textTransform: 'uppercase', fontWeight: 600, letterSpacing: 0.5, display: 'block', minHeight: 32, lineHeight: 1.2, mb: 0.5 }}>
              Ready to Advance
            </Typography>
            <Box sx={{ width: '100%', height: 1, bgcolor: 'divider', mb: 1 }} />
            <Typography variant="h5" sx={{ fontWeight: 600 }}>{kpis.readyToAdvance}</Typography>
          </Box>
        </Grid>
        <Grid item xs={6} sm={4} md={3} lg={1.5}>
          <Box
            sx={{
              textAlign: 'center',
              cursor: 'pointer',
              p: 1,
              borderRadius: 1,
              transition: 'all 0.2s',
              bgcolor: drillDownFilter?.type === 'backlog' ? 'primary.light' : 'transparent',
              '&:hover': {
                bgcolor: 'action.hover',
              },
            }}
            onClick={() => {
              if (drillDownFilter?.type === 'backlog') {
                setDrillDownFilter(null);
              } else {
                setDrillDownFilter({
                  type: 'backlog',
                  label: 'Backlog > 48 Hours',
                  customFilter: (r) => {
                    const metrics = calculateDerivedMetrics(r);
                    return metrics.isBacklog;
                  },
                });
              }
            }}
          >
            <Typography variant="caption" color="grey.500" sx={{ textTransform: 'uppercase', fontWeight: 600, letterSpacing: 0.5, display: 'block', minHeight: 32, lineHeight: 1.2, mb: 0.5 }}>
              Backlog &gt; 48h
            </Typography>
            <Box sx={{ width: '100%', height: 1, bgcolor: 'divider', mb: 1 }} />
            <Typography variant="h5" sx={{ fontWeight: 600 }}>{kpis.backlog}</Typography>
          </Box>
        </Grid>
        <Grid item xs={6} sm={4} md={3} lg={1.5}>
          <Box
            sx={{
              textAlign: 'center',
              cursor: 'pointer',
              p: 1,
              borderRadius: 1,
              transition: 'all 0.2s',
              bgcolor: drillDownFilter?.type === 'myCases' ? 'primary.light' : 'transparent',
              '&:hover': {
                bgcolor: 'action.hover',
              },
            }}
            onClick={() => {
              if (drillDownFilter?.type === 'myCases') {
                setDrillDownFilter(null);
              } else {
                setDrillDownFilter({
                  type: 'myCases',
                  label: 'My Active Cases',
                  customFilter: (r) => r.ownerId === currentUser?.id && !r.transitionedAt && r.currentStage !== WorkflowStage.DISQUALIFIED,
                });
              }
            }}
          >
            <Typography variant="caption" color="grey.500" sx={{ textTransform: 'uppercase', fontWeight: 600, letterSpacing: 0.5, display: 'block', minHeight: 32, lineHeight: 1.2, mb: 0.5 }}>
              My Active Cases
            </Typography>
            <Box sx={{ width: '100%', height: 1, bgcolor: 'divider', mb: 1 }} />
            <Typography variant="h5" sx={{ fontWeight: 600 }}>{workloadMetrics.activeCases}</Typography>
          </Box>
        </Grid>
        <Grid item xs={6} sm={4} md={3} lg={1.5}>
          <Box sx={{ textAlign: 'center', p: 1 }}>
            <Typography variant="caption" color="grey.500" sx={{ textTransform: 'uppercase', fontWeight: 600, letterSpacing: 0.5, display: 'block', minHeight: 32, lineHeight: 1.2, mb: 0.5 }}>
              Median Time to Review
            </Typography>
            <Box sx={{ width: '100%', height: 1, bgcolor: 'divider', mb: 1 }} />
            <Typography variant="h5" sx={{ fontWeight: 600 }}>{workloadMetrics.medianReviewTimeHours.toFixed(1)}h</Typography>
          </Box>
        </Grid>
        <Grid item xs={6} sm={4} md={3} lg={1.5}>
          <Box sx={{ textAlign: 'center', p: 1 }}>
            <Typography variant="caption" color="grey.500" sx={{ textTransform: 'uppercase', fontWeight: 600, letterSpacing: 0.5, display: 'block', minHeight: 32, lineHeight: 1.2, mb: 0.5 }}>
              Avg Cases Per Day
            </Typography>
            <Box sx={{ width: '100%', height: 1, bgcolor: 'divider', mb: 1 }} />
            <Typography variant="h5" sx={{ fontWeight: 600 }}>{workloadMetrics.casesPerDay.toFixed(1)}</Typography>
          </Box>
        </Grid>
        <Grid item xs={6} sm={4} md={3} lg={1.5}>
          <Box sx={{ textAlign: 'center', p: 1 }}>
            <Typography variant="caption" color="grey.500" sx={{ textTransform: 'uppercase', fontWeight: 600, letterSpacing: 0.5, display: 'block', minHeight: 32, lineHeight: 1.2, mb: 0.5 }}>
              Override Rate
            </Typography>
            <Box sx={{ width: '100%', height: 1, bgcolor: 'divider', mb: 1 }} />
            <Typography variant="h5" sx={{ fontWeight: 600 }}>{workloadMetrics.overrideRate.toFixed(1)}%</Typography>
          </Box>
        </Grid>
      </Grid>

      {/* Filter Indicator */}
      {drillDownFilter && (
        <Box
          sx={{
            mb: 3,
            p: 2,
            bgcolor: 'primary.light',
            borderRadius: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            border: '2px solid',
            borderColor: 'primary.main',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography variant="body1" sx={{ fontWeight: 600, color: 'primary.dark' }}>
              Filtered View: {drillDownFilter.label}
            </Typography>
            <Chip
              label={`${displayRecords.length} patient${displayRecords.length !== 1 ? 's' : ''}`}
              color="primary"
              size="small"
              sx={{ fontWeight: 600 }}
            />
          </Box>
          <Chip
            label="Clear Filter"
            onClick={() => setDrillDownFilter(null)}
            onDelete={() => setDrillDownFilter(null)}
            color="primary"
            variant="outlined"
            sx={{ cursor: 'pointer', fontWeight: 600 }}
          />
        </Box>
      )}

      {/* Row 2: Workflow Funnel */}
      <Box sx={{ mb: 3 }}>
        <ChartContainer
          title="Workflow Funnel"
          subtitle={`Patient progression through discharge workflow stages`}
          tooltip="Current: patients in each stage now | Median time: typical time spent in stage | At risk: patients taking significantly longer than average (>1 standard deviation) | Discharged: successfully completed workflow and transitioned to home care programs"
          height={260}
        >
          <WorkflowFunnel
            stages={funnelData}
            activeStage={
              drillDownFilter?.type === 'stage' && drillDownFilter.stage
                ? drillDownFilter.stage.replace(/_/g, ' ')
                : drillDownFilter?.label === 'Discharged Patients'
                ? 'DISCHARGED'
                : null
            }
            onStageClick={(stageName) => {
              // If clicking the same stage, clear the filter (toggle off)
              const currentActiveStage =
                drillDownFilter?.type === 'stage' && drillDownFilter.stage
                  ? drillDownFilter.stage.replace(/_/g, ' ')
                  : drillDownFilter?.label === 'Discharged Patients'
                  ? 'DISCHARGED'
                  : null;

              if (currentActiveStage === stageName) {
                setDrillDownFilter(null);
                return;
              }

              // Map display name back to WorkflowStage enum or special case
              const stageMap: Record<string, WorkflowStage | 'DISCHARGED'> = {
                'TRIAGE': WorkflowStage.TRIAGE,
                'ELIGIBILITY': WorkflowStage.ELIGIBILITY,
                'ACCEPTANCE': WorkflowStage.ACCEPTANCE,
                'TRANSITION': WorkflowStage.TRANSITION,
                'DISCHARGED': 'DISCHARGED',
              };

              const mappedStage = stageMap[stageName];

              if (mappedStage === 'DISCHARGED') {
                setDrillDownFilter({
                  type: 'custom',
                  label: 'Discharged Patients',
                  customFilter: (r) => !!r.transitionedAt,
                });
              } else if (mappedStage) {
                setDrillDownFilter({
                  type: 'stage',
                  label: `Stage: ${stageName}`,
                  stage: mappedStage,
                });
              }
            }}
          />
        </ChartContainer>
      </Box>

      {/* Row 3: Blockers & Disqualifications */}
      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <ChartContainer
            title="Top 5 Readiness Blockers"
            tooltip="Most common blockers preventing workflow advancement"
            height={280}
          >
            {blockerData.length > 0 ? (
              <BlockersChart
                blockers={blockerData}
                maxItems={5}
                onBlockerClick={(blocker) => {
                  // Toggle off if clicking same blocker
                  if (drillDownFilter?.label === `Blocker: ${blocker}`) {
                    setDrillDownFilter(null);
                    return;
                  }
                  setDrillDownFilter({
                    type: 'custom',
                    label: `Blocker: ${blocker}`,
                    customFilter: (r) => r.blockers.includes(blocker),
                  });
                }}
                activeBlocker={
                  drillDownFilter?.label?.startsWith('Blocker: ')
                    ? drillDownFilter.label.replace('Blocker: ', '')
                    : null
                }
              />
            ) : (
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                No blockers in current filter range
              </Box>
            )}
          </ChartContainer>
        </Grid>
        <Grid item xs={12} md={6}>
          <ChartContainer
            title="Disqualification Reasons"
            tooltip="Distribution of reasons for patient disqualification"
            height={280}
          >
            {disqualificationData.length > 0 ? (
              <DisqualificationChart
                data={disqualificationData}
                onReasonClick={(reason) => {
                  // Toggle off if clicking same reason
                  if (drillDownFilter?.label === `Disqualification: ${reason}`) {
                    setDrillDownFilter(null);
                    return;
                  }
                  // Convert display format (spaces) back to enum format (underscores) for filtering
                  const enumReason = reason.replace(/ /g, '_');
                  setDrillDownFilter({
                    type: 'custom',
                    label: `Disqualification: ${reason}`,
                    customFilter: (r) => r.disqualificationReason === enumReason,
                  });
                }}
                activeReason={
                  drillDownFilter?.label?.startsWith('Disqualification: ')
                    ? drillDownFilter.label.replace('Disqualification: ', '')
                    : null
                }
              />
            ) : (
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                No disqualifications in current filter range
              </Box>
            )}
          </ChartContainer>
        </Grid>
      </Grid>

      {/* Patient Details Table */}
      <Box sx={{ mb: 12 }}>
        <AnalyticsPatientTable
          patients={filteredRecords}
          drillDownFilter={drillDownFilter}
          onClearFilter={() => setDrillDownFilter(null)}
        />
      </Box>

      {/* AI Insights - Fixed Bottom */}
      <AIInsights
        insights={aiInsights.insights}
        recommendations={aiInsights.recommendations}
        summary={aiInsights.summary}
        onExpandedChange={setAiInsightsExpanded}
        onDataPointClick={(dataPoint) => {
          // Parse the data point and set appropriate filter
          if (dataPoint.includes('high priority patients')) {
            setDrillDownFilter({
              type: 'highPriority',
              label: 'High Priority Patients (Score ≥ 75)',
              customFilter: (r) => r.programScore >= 75 && r.currentStage !== WorkflowStage.DISQUALIFIED && !r.transitionedAt,
            });
          } else if (dataPoint.includes('backlog patient')) {
            setDrillDownFilter({
              type: 'backlog',
              label: 'Backlog > 48 Hours',
              customFilter: (r) => {
                const metrics = calculateDerivedMetrics(r);
                return metrics.isBacklog;
              },
            });
          } else if (dataPoint.includes('ready patients')) {
            setDrillDownFilter({
              type: 'ready',
              label: 'Ready to Advance (100% Readiness)',
              customFilter: (r) => r.readinessPercent === 100 && r.currentStage !== WorkflowStage.TRANSITION && !r.transitionedAt,
            });
          } else if (dataPoint.includes('active cases')) {
            setDrillDownFilter({
              type: 'myCases',
              label: 'My Active Cases',
              customFilter: (r) => r.ownerId === currentUser?.id && !r.transitionedAt && r.currentStage !== WorkflowStage.DISQUALIFIED,
            });
          } else if (dataPoint.includes('new candidates')) {
            const now = new Date();
            const last24h = new Date(now.getTime() - 24 * 60 * 60 * 1000);
            setDrillDownFilter({
              type: 'newCandidates',
              label: 'New Candidates (Last 24 Hours)',
              customFilter: (r) => new Date(r.admissionTimestamp) >= last24h,
            });
          }
        }}
      />
    </Box>
  );
};

export default OpsDashboard;
